import React, { useEffect, useMemo, useRef, useState } from 'react';
import { arc, pie } from 'd3-shape';
import { useTheme } from '../../../contexts/ThemeContext';
import { getChartTheme } from '../charting/chartTheme';

import { ReactComponent as BotIcon } from '../../../assets/icons/bot.svg';
import { ReactComponent as HumanIcon } from '../../../assets/icons/human.svg';

const PieChart = ({ data, text, disabled, isDarkMode: externalIsDarkMode = null }) => {
  const { isDarkMode: contextIsDarkMode } = useTheme();
  const isDarkMode = externalIsDarkMode ?? contextIsDarkMode;
  const theme = getChartTheme(isDarkMode);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 320, height: 320 });
  const [uiScale, setUiScale] = useState(1);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof window === 'undefined') return undefined;

    let frameId = null;
    const measure = () => {
      const nextWidth = node.clientWidth || 320;
      const nextHeight = node.clientHeight || 320;
      const diameter = Math.max(220, Math.min(nextWidth, nextHeight));
      const localScale = Math.max(0.75, Math.min(1.2, diameter / 320));
      setDimensions({
        width: nextWidth,
        height: nextHeight,
      });
      setUiScale(localScale);
    };

    const scheduleMeasure = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        measure();
      });
    };

    const width = node.clientWidth || 320;
    const height = node.clientHeight || 320;
    setDimensions({ width, height });
    measure();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleMeasure)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(node);
    } else {
      window.addEventListener('resize', scheduleMeasure);
    }

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      if (resizeObserver) resizeObserver.disconnect();
      if (!resizeObserver) {
        window.removeEventListener('resize', scheduleMeasure);
      }
    };
  }, []);

  const { width, height } = dimensions;
  const diameter = Math.max(220, Math.min(width, height));
  const radiusScale = diameter / 340;
  const hoverRadius = Math.max(8, Math.round(10 * uiScale));
  const centerFontSize = Math.max(12, Math.round(18 * uiScale));
  const lineGap = Math.max(18, Math.round(24 * uiScale));
  const badgeFontSize = Math.max(12, Math.round(14 * uiScale));
  const badgePaddingY = Math.max(4, Math.round(4 * uiScale));
  const badgePaddingX = Math.max(6, Math.round(8 * uiScale));
  const badgeGap = Math.max(6, Math.round(8 * uiScale));
  const iconSize = Math.max(18, Math.round(24 * uiScale));
  const badgeRadius = Math.max(4, Math.round(5 * uiScale));
  const strokeWidth = Math.max(2, Math.round(2.2 * uiScale));

  const chartData = useMemo(() => {
    const pieGenerator = pie()
      .value((item) => item.value)
      .startAngle(120 * (Math.PI / 180))
      .endAngle(480 * (Math.PI / 180));

    return pieGenerator(data);
  }, [data]);

  const labelPositions = useMemo(() => {
    const baseArc = arc()
      .cornerRadius(3)
      .innerRadius((item) => item.data.innerRadius * radiusScale)
      .outerRadius((item) => item.data.outerRadius * radiusScale);

    return chartData.map((item) => ({
      x: baseArc.centroid(item)[0],
      y: baseArc.centroid(item)[1],
      data: item.data,
    }));
  }, [chartData, radiusScale]);

  useEffect(() => {
    setActiveIndex(null);
  }, [data, disabled, text, isDarkMode]);

  return (
    <div ref={containerRef}
         style={{ position: 'relative',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}>
      <svg
           style={{
             position: 'absolute',
             top: 0,
             left: 0,
             width: '100%',
             height: '100%',
           }}
           viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${width / 2}, ${height / 2})`}>
          {chartData.map((slice, index) => {
            const isActive = activeIndex === index && !disabled;
            const sliceArc = arc()
              .cornerRadius(3)
              .innerRadius(slice.data.innerRadius * radiusScale)
              .outerRadius(slice.data.outerRadius * radiusScale + (isActive ? hoverRadius : 0));

            return (
              <path
                key={`${slice.data.label}-${index}`}
                d={sliceArc(slice) || ''}
                fill={disabled ? theme.neutralMuted : slice.data.color}
                stroke={theme.surfaceLow}
                strokeWidth={strokeWidth}
                onMouseEnter={() => {
                  if (!disabled) setActiveIndex(index);
                }}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {!disabled ? <title>{`${slice.data.label}: ${slice.data.value}%`}</title> : null}
              </path>
            );
          })}
          <text
            textAnchor="middle"
            dominantBaseline="middle"
            x="0"
            y="0"
            style={{
              fontSize: `${centerFontSize}px`,
              fontWeight: 700,
              fill: disabled ? theme.neutralHigh : theme.textHigh,
            }}
          >
            {text.split('\n').map((line, index) => (
              <tspan
                key={`${line}-${index}`}
                x="0"
                dy={index * lineGap - lineGap * 0.35}
              >
                {line}
              </tspan>
            ))}
          </text>
        </g>
      </svg>

      {labelPositions.map((pos, index) => (
        <div
          key={`${pos.data.label}-${index}`}
          style={{
            position: 'absolute',
            top: `calc(50% + ${pos.y}px)`,
            left: `calc(50% + ${pos.x}px)`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.tooltipBg,
            color: theme.tooltipText,
            border: `1px solid ${theme.strokeLow}`,
            boxShadow: isDarkMode
              ? '0 0.25rem 0.9rem rgba(0, 0, 0, 0.35)'
              : '0 0.25rem 0.9rem rgba(21, 28, 45, 0.12)',
            borderRadius: `${badgeRadius}px`,
            padding: `${badgePaddingY}px ${badgePaddingX}px`,
            fontSize: `${badgeFontSize}px`,
            fontWeight: 700,
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}>
          <div style={{ display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: `${badgeGap}px`,
                        padding: `0 ${badgePaddingY}px`,
                      }}>
            {pos.data.label.startsWith('Bot') ?
             <BotIcon style={{ width: `${iconSize}px`,
                               height: `${iconSize}px`,
                               fill: disabled ? theme.neutralMuted : pos.data.color,
                             }} />
             :
             <HumanIcon style={{ width: `${iconSize}px`,
                                 height: `${iconSize}px`,
                                 fill: disabled ? theme.neutralMuted : pos.data.color,
                               }} />
            }
            {!disabled && <span>{pos.data.value}%</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default PieChart;
